import { ReactNode, createElement } from 'react';
import { FileType } from '@/types/common';
import { PaginationParams } from '@/types/request';

/**
 * @Author: YuKi
 * @description: 去除路由后面的 /*
 * @param {string} menuUrl
 * @return {*}
 */
export const getPureMenuUrl = (menuUrl: string) => {
  if (menuUrl.endsWith('/*')) return menuUrl.slice(0, -2);
  return menuUrl;
};

/**
 * @Author: Stephen
 * @description: 提取富文本
 * @param {string} html
 * @return {*}
 */
export const extractRichText = (html: string) => {
  const richText = html.replace(/<p>/g, '').replace(/<\/p>/g, '');
  const finalRichText = richText.replace(/<img[^>]*>/g, '');
  return finalRichText;
};

/**
 * @Author: YuKi
 * @description: 根据key获取url上的固定参数链接
 * @param {string} url
 * @return {*}
 */
export const getQueryParams = (key: string) => {
  const queryString = window.location.search;
  const params = new URLSearchParams(queryString);
  if (params) {
    return params.get(key) || undefined;
  }
  return undefined;
};

/** 凭证类查询参数：都是一次性的，被消费后必须立刻从地址栏和路由状态里一起抹掉（ticket 见 utils/boot，token 见 layout/init） */
export const CREDENTIAL_QUERY_KEYS = ['ticket', 'endPoint', 'token', 'Authorization', 'authorization'];

/** 去掉凭证类参数后的 search（带前导 ?，没有剩余参数时返回空串） */
export const stripCredentialParams = (search: string) => {
  const params = new URLSearchParams(search);
  CREDENTIAL_QUERY_KEYS.forEach((key) => params.delete(key));
  const next = params.toString();
  return next ? `?${next}` : '';
};

/**
 * @Author: YuKi
 * @description: 下载
 * @param {string} url
 * @param {string} fileName
 * @param {FileType} fileType
 * @return {*}
 */
export const downloadFileStream = (url: string, fileName: string, fileType: FileType) => {
  // 创建一个隐藏的 <a> 标签并触发下载
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.style.display = 'none';
  if (fileType === FileType.image || fileType === FileType.video || fileType === FileType.pdf) {
    link.target = '_blank';
  }
  document.body.appendChild(link);
  link.click();
  // 清理
  document.body.removeChild(link);
};

/**
 * @Author: YuKi
 * @description: 根据token解析的sdc，判断是哪个数据中心
 * @param {string} url
 * @return {*}
 */
// export const fineDataCenter = (token: string) => {
//   const jwtToken: ITokenItem = jwtDecode(token);
//   if (typeof jwtToken.sdc === 'number') {
//     const currentDataCenter = config.dataCenter.find((item) => item.value === jwtToken.sdc);
//     setDataCenter(import.meta.env[`VITE_DATA_CENTER_${currentDataCenter?.key}`]);
//   } else if (typeof jwtToken.sdc === 'string') {
//     setDataCenter(import.meta.env[`VITE_DATA_CENTER_${jwtToken.sdc}`]);
//   }
// };

/**
 * @Author: Stephen
 * @description: 下载blob文件
 * @param {string} url 文件URL
 * @param {string} fileName 文件名
 * @param {RequestInit} options 可选的fetch配置
 */
/** 将后端返回的二进制 Blob 触发浏览器下载 */
export const saveBlob = (blob: Blob, fileName: string) => {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
};

/**
 * 从文件名或 URL 中取扩展名（去掉 query/hash，无扩展名返回空串）。
 * 用于下载时拼正确后缀，避免误用业务类型 fileType（如 IMAGE/VIDEO）当后缀导致 ".IMAGE"。
 */
export const getFileExtension = (nameOrUrl?: string): string => {
  if (!nameOrUrl) return '';
  const path = nameOrUrl.split(/[?#]/)[0];
  const base = path.substring(path.lastIndexOf('/') + 1);
  const dot = base.lastIndexOf('.');
  return dot > 0 ? base.slice(dot + 1) : '';
};

/** 文件大小：字节自动换算到合适单位（B/KB/MB/GB），保留两位小数 */
export const formatFileSize = (size: number): string => {
  const units = ['B', 'KB', 'MB', 'GB'];
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(2)} ${units[unitIndex]}`;
};

export const handleDownload = async (url: string, fileName: string, suffix: string, options?: RequestInit) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new Error('This function can only be used in browser environment');
  }

  const response = await fetch(url, options);
  if (!response.ok) {
    throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
  }

  if (!response.body) {
    throw new Error('Readable stream is not available on the current environment');
  }

  const reader = response.body.getReader();
  const chunks: BlobPart[] = [];
  let streamDone = false;
  while (!streamDone) {
    const { done, value } = await reader.read();
    streamDone = done;
    if (!done && value) {
      chunks.push(value);
    }
  }

  // 固定 octet-stream，不要改回响应头里的真实 Content-Type：Safari 对 blob URL 会按 MIME
  // 判断能否内置渲染，PDF/图片/纯文本这类能渲染的会忽略 a.download 直接在当前页打开。
  // 文件名后缀由下面的 link.download 给出，系统按扩展名识别，丢掉真实 MIME 不影响。
  const blob = new Blob(chunks, { type: 'application/octet-stream' });
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = `${fileName}.${suffix}`;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
};

/**
 * 将文本中命中关键词的片段用 <mark> 包裹。
 * 关键词按 空格 / - / _ / & / 斜杠 拆词（与后端 ES .std 断词口径对齐，使高亮覆盖贴合搜索命中），
 * 但渲染时把"被这些分隔符连接的连续命中片段"合并成一个整体高亮（含中间的分隔符），
 * 例如搜 "datasheet_smile-g3" 会整体高亮 "Datasheet_SMILE-G3"，而不是拆成 datasheet / smile / g3 三段碎块；
 * 同时仍支持子串命中（搜 "s5" 能高亮 "S5_DE" 里的 S5）。
 */
export const highlightText = (text: string, keyword: string): ReactNode => {
  const raw = (text ?? '').toString();
  const tokens = (keyword ?? '')
    .trim()
    .split(/[\s\-_&/]+/)
    .filter(Boolean);
  if (tokens.length === 0) return raw;
  // 任一 token（转义正则特殊字符）
  const escaped = tokens.map((tk) => tk.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const tokenAlt = `(?:${escaped.join('|')})`;
  // 连续命中片段：token 之后可跟"分隔符 + token"若干次，整段作为一个高亮单元
  const runPattern = `${tokenAlt}(?:[\\s\\-_&/]+${tokenAlt})*`;
  const splitRe = new RegExp(`(${runPattern})`, 'ig');
  const fullRunRe = new RegExp(`^${runPattern}$`, 'i');
  const parts = raw.split(splitRe);
  return parts.map((part, i) => (part && fullRunRe.test(part) ? createElement('mark', { key: i, className: 'search-hl' }, part) : part));
};

/*
 * @Description: 产品详情页 URL 段（slug + base36 压缩 ID）的构造与解析
 * @Author: liushichuan
 *
 * URL 段格式：{slug-of-name}-p{base36-id}
 *   例：110000000000172 → base36 → z9ce3xwu0 → smile-g3-8kw-pz9ce3xwu0
 * 注：当前 ID < Number.MAX_SAFE_INTEGER (9e15) 时安全；超出请改用 BigInt
 */

/**
 * 把名称转成 URL-safe 的 slug：
 *  - 小写
 *  - 非字母/数字字符（含 Unicode 字母）都换成 -
 *  - 连续 - 折叠成单个
 *  - 去掉首尾 -
 */
export const slugify = (name: string): string => {
  if (!name) return '';
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
};

/**
 * 从产品 id（如 "PRODUCT-110000000000172"）取出尾部纯数字部分
 */
const extractNumericId = (id: string): string => {
  if (!id) return '';
  const parts = id.split('-');
  return parts[parts.length - 1];
};

/** 十进制字符串数字 → base36（0-9a-z），把长 ID 压短 */
const encodeIdToBase36 = (numericId: string): string => {
  const n = Number(numericId);
  if (!Number.isFinite(n) || n < 0) return numericId;
  return n.toString(36);
};

/** base36 → 十进制字符串数字 */
const decodeIdFromBase36 = (encoded: string): string => {
  const n = parseInt(encoded, 36);
  if (!Number.isFinite(n)) return '';
  return n.toString(10);
};

/**
 * 构造产品详情页的 URL 段：smile-g3-8kw-p{base36}
 */
export const buildProductSlug = (product: { id: string; name: string }): string => {
  const encodedId = encodeIdToBase36(extractNumericId(product.id));
  const slug = slugify(product.name);
  return slug ? `${slug}-p${encodedId}` : `p${encodedId}`;
};

/**
 * 反解析 URL 段，返回产品的（十进制字符串）数字 ID。
 * 支持两种格式：
 *   1. 标准：xxx-p{base36}
 *   2. 兜底：直接传十进制纯数字段（手填的极简形态）
 */
export const parseProductSlug = (segment: string): { numericId: string } | null => {
  if (!segment) return null;
  const decoded = decodeURIComponent(segment);

  // 标准：尾部 -p{base36}
  const m = decoded.match(/-p([0-9a-z]+)$/i);
  if (m) {
    const numericId = decodeIdFromBase36(m[1].toLowerCase());
    return numericId ? { numericId } : null;
  }

  // 纯数字段
  if (/^\d+$/.test(decoded)) return { numericId: decoded };

  return null;
};

/** 表格序号列：按分页参数换算成全局序号 */
export const addTableOrder = (index: number, params: PaginationParams) => {
  const { pageIndex = 1, pageSize = 10 } = params;
  return index + (pageIndex - 1) * pageSize + 1;
};
